import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [recentFeedback, setRecentFeedback] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [listeners, projects, tracks, remixes, versions, ratings, fb] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'listener'),
        supabase.from('projects').select('id', { count: 'exact', head: true }),
        supabase.from('tracks').select('id', { count: 'exact', head: true }),
        supabase.from('remixes').select('id', { count: 'exact', head: true }),
        supabase.from('track_versions').select('id', { count: 'exact', head: true }),
        supabase.from('ratings').select('id', { count: 'exact', head: true }),
        supabase.from('feedback_with_author').select('*').order('created_at', { ascending: false }).limit(10),
      ]);

      setStats({
        totalListeners: listeners.count || 0,
        totalProjects: projects.count || 0,
        totalTracks: tracks.count || 0,
        totalRemixes: remixes.count || 0,
        totalVersions: versions.count || 0,
        totalRatings: ratings.count || 0,
        totalFeedback: fb.data?.length || 0,
      });
      setRecentFeedback(fb.data || []);
    };
    load();
  }, []);

  if (!stats) return <p className="text-secondary">Loading...</p>;

  return (
    <div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-value">{stats.totalListeners}</div><div className="stat-label">Listeners</div></div>
        <div className="stat-card"><div className="stat-value">{stats.totalProjects}</div><div className="stat-label">Projects</div></div>
        <div className="stat-card"><div className="stat-value">{stats.totalTracks}</div><div className="stat-label">Songs</div></div>
        <div className="stat-card"><div className="stat-value">{stats.totalRemixes}</div><div className="stat-label">Remixes</div></div>
        <div className="stat-card"><div className="stat-value">{stats.totalVersions}</div><div className="stat-label">Versions</div></div>
        <div className="stat-card"><div className="stat-value">{stats.totalRatings}</div><div className="stat-label">Ratings</div></div>
      </div>

      <h3 className="mb-8">Recent Feedback</h3>
      {recentFeedback.length === 0 ? (
        <p className="text-secondary text-sm">No feedback yet</p>
      ) : (
        recentFeedback.map(fb => (
          <div key={fb.id} className="feedback-item">
            <div className="feedback-header">
              <span className="feedback-author">{fb.display_name}</span>
              <span className="feedback-time">{new Date(fb.created_at).toLocaleDateString()}</span>
            </div>
            {fb.emotion && (
              <span className="badge badge-accent" style={{ marginBottom: 4, display: 'inline-block' }}>{fb.emotion}</span>
            )}
            <div className="feedback-text">{fb.comment}</div>
          </div>
        ))
      )}
    </div>
  );
}

function ProjectsManager() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newProject, setNewProject] = useState({ title: '', description: '', coverFile: null });
  const [expandedProject, setExpandedProject] = useState(null);
  const [projectDetails, setProjectDetails] = useState({});
  const [uploading, setUploading] = useState(false);

  const [addingTrackFor, setAddingTrackFor] = useState(null);
  const [trackForm, setTrackForm] = useState({ title: '', remix_title: '', version_label: '', file: null });
  const [addingRemixFor, setAddingRemixFor] = useState(null);
  const [remixForm, setRemixForm] = useState({ title: '', version_label: '', file: null });
  const [addingVersionFor, setAddingVersionFor] = useState(null);
  const [versionForm, setVersionForm] = useState({ version_label: '', file: null });

  const loadProjects = useCallback(async () => {
    const { data } = await supabase
      .from('projects_with_stats')
      .select('*')
      .order('created_at', { ascending: false });
    setProjects(data || []);
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const loadProjectDetail = async (projectId) => {
    const { data: tracks } = await supabase
      .from('tracks')
      .select('*, remixes:remixes_with_stats(*, versions:versions_with_stats(*))')
      .eq('project_id', projectId)
      .order('track_number');

    const sortedTracks = (tracks || []).map(t => ({
      ...t,
      remixes: (t.remixes || []).sort((a, b) => a.remix_number - b.remix_number).map(r => ({
        ...r, versions: (r.versions || []).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      }))
    }));

    setProjectDetails(prev => ({ ...prev, [projectId]: { tracks: sortedTracks } }));
  };

  const uploadCover = async (file) => {
    const ext = file.name.split('.').pop();
    const filename = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from('covers').upload(filename, file);
    if (error) throw error;
    return filename;
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    let cover_image = null;
    if (newProject.coverFile) {
      cover_image = await uploadCover(newProject.coverFile);
    }
    await supabase.from('projects').insert({ title: newProject.title, description: newProject.description, cover_image, created_by: user.id });
    setNewProject({ title: '', description: '', coverFile: null });
    setShowCreate(false);
    loadProjects();
  };

  const uploadFileAndCreateVersion = async (file, remixId, versionLabel) => {
    const ext = file.name.split('.').pop();
    const filename = `${crypto.randomUUID()}.${ext}`;
    const { error: uploadErr } = await supabase.storage.from('tracks').upload(filename, file);
    if (uploadErr) throw uploadErr;

    const isVideo = file.type.startsWith('video/');
    await supabase.from('track_versions').insert({
      remix_id: remixId,
      version_label: versionLabel || 'v1',
      filename,
      original_name: file.name,
      file_size: file.size,
      mime_type: file.type || 'audio/mpeg',
      media_type: isVideo ? 'video' : 'audio',
      uploaded_by: user.id,
    });
  };

  const handleAddTrack = async (e, projectId) => {
    e.preventDefault();
    if (!trackForm.file) return;
    setUploading(true);
    try {
      const title = trackForm.title || trackForm.file.name.replace(/\.[^.]+$/, '');
      const { data: existingTracks } = await supabase
        .from('tracks').select('track_number').eq('project_id', projectId).order('track_number', { ascending: false }).limit(1);
      const nextNum = (existingTracks?.[0]?.track_number || 0) + 1;

      const { data: track } = await supabase.from('tracks')
        .insert({ project_id: projectId, title, track_number: nextNum })
        .select().single();

      const { data: remix } = await supabase.from('remixes')
        .insert({ track_id: track.id, title: trackForm.remix_title || 'Original', remix_number: 1 })
        .select().single();

      await uploadFileAndCreateVersion(trackForm.file, remix.id, trackForm.version_label || 'v1');

      setAddingTrackFor(null);
      setTrackForm({ title: '', remix_title: '', version_label: '', file: null });
      loadProjects();
      if (expandedProject === projectId) loadProjectDetail(projectId);
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const handleAddRemix = async (e, trackId) => {
    e.preventDefault();
    if (!remixForm.file) return;
    setUploading(true);
    try {
      const { data: existingRemixes } = await supabase
        .from('remixes').select('remix_number').eq('track_id', trackId).order('remix_number', { ascending: false }).limit(1);
      const nextNum = (existingRemixes?.[0]?.remix_number || 0) + 1;

      const { data: remix } = await supabase.from('remixes')
        .insert({ track_id: trackId, title: remixForm.title || `Remix ${nextNum}`, remix_number: nextNum })
        .select().single();

      await uploadFileAndCreateVersion(remixForm.file, remix.id, remixForm.version_label || 'v1');

      setAddingRemixFor(null);
      setRemixForm({ title: '', version_label: '', file: null });
      loadProjects();
      if (expandedProject) loadProjectDetail(expandedProject);
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const handleAddVersion = async (e, remixId) => {
    e.preventDefault();
    if (!versionForm.file) return;
    setUploading(true);
    try {
      const { data: existingVersions } = await supabase
        .from('track_versions').select('version_label').eq('remix_id', remixId).order('created_at', { ascending: false }).limit(1);
      const lastLabel = existingVersions?.[0]?.version_label || 'v0';
      const nextNum = parseInt(lastLabel.replace(/\D/g, '') || '0') + 1;

      await uploadFileAndCreateVersion(versionForm.file, remixId, versionForm.version_label || `v${nextNum}`);

      setAddingVersionFor(null);
      setVersionForm({ version_label: '', file: null });
      loadProjects();
      if (expandedProject) loadProjectDetail(expandedProject);
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const handleDeleteTrack = async (trackId, projectId) => {
    if (!window.confirm('Delete this song and ALL its remixes/versions?')) return;
    await supabase.from('tracks').delete().eq('id', trackId);
    loadProjects();
    loadProjectDetail(projectId);
  };

  const handleDeleteRemix = async (remixId, projectId) => {
    if (!window.confirm('Delete this remix and all its versions?')) return;
    await supabase.from('remixes').delete().eq('id', remixId);
    loadProjects();
    loadProjectDetail(projectId);
  };

  const handleDeleteVersion = async (versionId, projectId) => {
    if (!window.confirm('Delete this version?')) return;
    const { data: version } = await supabase.from('track_versions').select('filename').eq('id', versionId).single();
    if (version?.filename) await supabase.storage.from('tracks').remove([version.filename]);
    await supabase.from('track_versions').delete().eq('id', versionId);
    loadProjects();
    loadProjectDetail(projectId);
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Delete this project and all its songs?')) return;
    await supabase.from('projects').delete().eq('id', projectId);
    loadProjects();
  };

  const toggleExpand = (projectId) => {
    if (expandedProject === projectId) {
      setExpandedProject(null);
    } else {
      setExpandedProject(projectId);
      if (!projectDetails[projectId]) loadProjectDetail(projectId);
    }
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <h3>Projects</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(!showCreate)}>+ New Project</button>
      </div>

      {showCreate && (
        <form className="card" onSubmit={handleCreateProject}>
          <div className="form-group">
            <label>Title</label>
            <input className="input" value={newProject.title}
              onChange={e => setNewProject(p => ({ ...p, title: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea className="input" value={newProject.description}
              onChange={e => setNewProject(p => ({ ...p, description: e.target.value }))}
              placeholder="What should listeners know about this project?" />
          </div>
          <div className="form-group">
            <label>Cover Image</label>
            <input type="file" accept="image/*"
              onChange={e => setNewProject(p => ({ ...p, coverFile: e.target.files[0] }))}
              style={{ color: 'var(--text-primary)' }} />
          </div>
          <div className="flex gap-8">
            <button className="btn btn-primary btn-sm">Create</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </form>
      )}

      {projects.map(p => (
        <div key={p.id} className="card">
          <div className="flex-between">
            <div style={{ cursor: 'pointer' }} onClick={() => toggleExpand(p.id)}>
              <div className="flex-center gap-8">
                <h3 style={{ fontSize: 16 }}>{p.title}</h3>
                <span className={`badge ${p.status === 'published' ? 'badge-success' : 'badge-warning'}`}>{p.status}</span>
              </div>
              <span className="text-secondary text-sm">
                {p.track_count} song{p.track_count !== 1 ? 's' : ''} &middot; {p.rating_count} rating{p.rating_count !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex gap-8">
              <button className={`btn btn-sm ${p.status === 'published' ? 'btn-secondary' : 'btn-primary'}`}
                onClick={async () => {
                  const newStatus = p.status === 'published' ? 'unpublished' : 'published';
                  await supabase.from('projects').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', p.id);
                  loadProjects();
                }}>
                {p.status === 'published' ? 'Unpublish' : 'Publish'}
              </button>
              <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                {p.cover_image ? 'Change Cover' : '+ Cover'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const cover_image = await uploadCover(file);
                  await supabase.from('projects').update({ cover_image }).eq('id', p.id);
                  loadProjects();
                }} />
              </label>
              <button className="btn btn-primary btn-sm" onClick={() => {
                setAddingTrackFor(addingTrackFor === p.id ? null : p.id);
                setAddingRemixFor(null);
                setAddingVersionFor(null);
              }}>+ Song</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDeleteProject(p.id)}>Delete</button>
            </div>
          </div>

          {addingTrackFor === p.id && (
            <form className="mt-16" onSubmit={(e) => handleAddTrack(e, p.id)} style={{
              background: 'var(--bg-elevated)', padding: 16, borderRadius: 'var(--radius-md)'
            }}>
              <div className="form-group">
                <label>Audio / Video File</label>
                <input type="file" accept=".mp3,.wav,.flac,.aac,.m4a,.ogg,.mp4,.mov,.webm,.mkv,.avi"
                  onChange={e => setTrackForm(f => ({ ...f, file: e.target.files[0] }))}
                  required style={{ color: 'var(--text-primary)' }} />
              </div>
              <div className="flex gap-8">
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Song Title</label>
                  <input className="input" value={trackForm.title}
                    onChange={e => setTrackForm(f => ({ ...f, title: e.target.value }))} placeholder="Auto-detect from filename" />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Remix Title</label>
                  <input className="input" value={trackForm.remix_title}
                    onChange={e => setTrackForm(f => ({ ...f, remix_title: e.target.value }))} placeholder="Original" />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Version Label</label>
                  <input className="input" value={trackForm.version_label}
                    onChange={e => setTrackForm(f => ({ ...f, version_label: e.target.value }))} placeholder="e.g. v1" />
                </div>
              </div>
              <div className="flex gap-8">
                <button className="btn btn-primary btn-sm" disabled={uploading}>{uploading ? 'Uploading...' : 'Add Song'}</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAddingTrackFor(null)}>Cancel</button>
              </div>
            </form>
          )}

          {expandedProject === p.id && projectDetails[p.id] && (
            <div className="mt-16">
              {projectDetails[p.id].tracks.map(track => (
                <div key={track.id} style={{ marginBottom: 16 }}>
                  <div className="flex-between" style={{
                    padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)'
                  }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>Song {track.track_number}: {track.title}</span>
                      <span className="badge badge-accent" style={{ marginLeft: 8 }}>
                        {track.remixes?.length || 0} remix{(track.remixes?.length || 0) !== 1 ? 'es' : ''}
                      </span>
                    </div>
                    <div className="flex gap-8">
                      <button className="btn btn-primary btn-sm" onClick={() => {
                        setAddingRemixFor(addingRemixFor === track.id ? null : track.id);
                        setAddingTrackFor(null);
                        setAddingVersionFor(null);
                      }}>+ Remix</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDeleteTrack(track.id, p.id)}>Delete Song</button>
                    </div>
                  </div>

                  {addingRemixFor === track.id && (
                    <form className="mt-8" onSubmit={(e) => handleAddRemix(e, track.id)} style={{
                      background: 'var(--bg-elevated)', padding: 12, borderRadius: 'var(--radius-md)', marginLeft: 16
                    }}>
                      <div className="form-group">
                        <label>Audio / Video File</label>
                        <input type="file" accept=".mp3,.wav,.flac,.aac,.m4a,.ogg,.mp4,.mov,.webm,.mkv,.avi"
                          onChange={e => setRemixForm(f => ({ ...f, file: e.target.files[0] }))}
                          required style={{ color: 'var(--text-primary)' }} />
                      </div>
                      <div className="flex gap-8">
                        <div className="form-group" style={{ flex: 1 }}>
                          <label>Remix Title</label>
                          <input className="input" value={remixForm.title}
                            onChange={e => setRemixForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. DJ Smith Remix" />
                        </div>
                        <div className="form-group" style={{ flex: 1 }}>
                          <label>Version Label</label>
                          <input className="input" value={remixForm.version_label}
                            onChange={e => setRemixForm(f => ({ ...f, version_label: e.target.value }))} placeholder="e.g. v1" />
                        </div>
                      </div>
                      <div className="flex gap-8">
                        <button className="btn btn-primary btn-sm" disabled={uploading}>{uploading ? 'Uploading...' : 'Add Remix'}</button>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAddingRemixFor(null)}>Cancel</button>
                      </div>
                    </form>
                  )}

                  {track.remixes?.map(remix => (
                    <div key={remix.id} style={{ marginLeft: 16, marginTop: 8 }}>
                      <div className="flex-between" style={{
                        padding: '8px 12px', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)'
                      }}>
                        <div>
                          <span style={{ fontWeight: 500, color: 'var(--color-primary)' }}>{remix.title}</span>
                          <span className="badge badge-accent" style={{ marginLeft: 8 }}>
                            {remix.version_count} version{remix.version_count !== 1 ? 's' : ''}
                          </span>
                          {remix.avg_rating > 0 && (
                            <span className="badge badge-accent" style={{ marginLeft: 8 }}>Avg: {remix.avg_rating}</span>
                          )}
                        </div>
                        <div className="flex gap-8">
                          <button className="btn btn-primary btn-sm" onClick={() => {
                            setAddingVersionFor(addingVersionFor === remix.id ? null : remix.id);
                            setAddingTrackFor(null);
                            setAddingRemixFor(null);
                          }}>+ Version</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDeleteRemix(remix.id, p.id)}>Delete Remix</button>
                        </div>
                      </div>

                      {addingVersionFor === remix.id && (
                        <form className="mt-8" onSubmit={(e) => handleAddVersion(e, remix.id)} style={{
                          background: 'var(--bg-elevated)', padding: 12, borderRadius: 'var(--radius-md)', marginLeft: 16
                        }}>
                          <div className="form-group">
                            <label>Audio / Video File</label>
                            <input type="file" accept=".mp3,.wav,.flac,.aac,.m4a,.ogg,.mp4,.mov,.webm,.mkv,.avi"
                              onChange={e => setVersionForm(f => ({ ...f, file: e.target.files[0] }))}
                              required style={{ color: 'var(--text-primary)' }} />
                          </div>
                          <div className="form-group">
                            <label>Version Label</label>
                            <input className="input" value={versionForm.version_label}
                              onChange={e => setVersionForm(f => ({ ...f, version_label: e.target.value }))}
                              placeholder="Auto-incremented if empty" />
                          </div>
                          <div className="flex gap-8">
                            <button className="btn btn-primary btn-sm" disabled={uploading}>{uploading ? 'Uploading...' : 'Upload Version'}</button>
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAddingVersionFor(null)}>Cancel</button>
                          </div>
                        </form>
                      )}

                      {remix.versions?.map(v => (
                        <div key={v.id} className="flex-between" style={{ padding: '6px 12px 6px 28px', marginTop: 4 }}>
                          <div>
                            <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{v.version_label}</span>
                            <span className="text-secondary text-sm" style={{ marginLeft: 8 }}>{v.original_name}</span>
                          </div>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDeleteVersion(v.id, p.id)}>Remove</button>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}

              {projectDetails[p.id].tracks.length === 0 && (
                <p className="text-secondary text-sm">No songs yet. Add a song to get started.</p>
              )}
            </div>
          )}
        </div>
      ))}

      {projects.length === 0 && (
        <div className="empty-state">
          <h3>No projects yet</h3>
          <p className="text-secondary">Create a project and add songs to get started</p>
        </div>
      )}
    </div>
  );
}

function ListenersManager() {
  const [listeners, setListeners] = useState([]);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'listener')
        .order('created_at', { ascending: false });
      setListeners(data || []);
    };
    load();
  }, []);

  return (
    <div>
      <h3 className="mb-16">Listeners</h3>
      {listeners.length === 0 ? (
        <p className="text-secondary">No listeners registered yet</p>
      ) : (
        listeners.map(l => (
          <div key={l.id} style={{
            padding: '10px 14px', background: 'var(--bg-elevated)',
            borderRadius: 'var(--radius-md)', marginBottom: 6
          }}>
            <div className="flex-between">
              <div>
                <span style={{ fontWeight: 600 }}>{l.display_name}</span>
                {l.phone && <span className="text-secondary text-sm" style={{ marginLeft: 8 }}>{l.phone}</span>}
              </div>
              <span className="text-secondary text-sm">
                Joined: {new Date(l.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default function AdminPage() {
  const [tab, setTab] = useState('dashboard');

  return (
    <div className="container mt-24">
      <h1 className="mb-16" style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-highlighted)', letterSpacing: '-0.3px' }}>
        Admin Dashboard
      </h1>

      <div className="tabs mb-16">
        <button className={`tab ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>Overview</button>
        <button className={`tab ${tab === 'projects' ? 'active' : ''}`} onClick={() => setTab('projects')}>Projects</button>
        <button className={`tab ${tab === 'listeners' ? 'active' : ''}`} onClick={() => setTab('listeners')}>Listeners</button>
      </div>

      {tab === 'dashboard' && <AdminDashboard />}
      {tab === 'projects' && <ProjectsManager />}
      {tab === 'listeners' && <ListenersManager />}
    </div>
  );
}
